import { useMutation, useQueryClient } from '@tanstack/react-query';
import { http } from '@/lib/http';

// "Convert to care-only" — the case-level action that stops flight rebooking on
// an EXISTING case (round 2 of the care-only feature; creation-time care-only
// lives on /cases/new). The BFF proxies to cases `POST /v1/cases/:id/care-only`,
// which is idempotent: it flags the case and stamps NOT_APPLICABLE onto every
// sub-case still unresolved (PENDING / OPTIONS_READY / NO_OPTIONS or no
// rebooking state at all), while groups with a CHOSEN flight (SELECTED /
// OVERRIDDEN) keep it — the response says how many of each.

export interface CareOnlyConversionResult {
  caseUrn: string;
  converted: boolean;
  /** Sub-cases stamped NOT_APPLICABLE by THIS call — 0 on a repeat call. */
  stamped: number;
  /** Groups left alone because they already have a chosen flight. */
  kept: number;
}

export async function convertCaseToCareOnly(caseUrn: string): Promise<CareOnlyConversionResult> {
  return http
    .post(`v1/cases/${encodeURIComponent(caseUrn)}/care-only`)
    .json<CareOnlyConversionResult>();
}

export function useConvertCareOnly(caseUrn: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => convertCaseToCareOnly(caseUrn),
    onSuccess: () => {
      // Every case-scoped read moves: the overview (the flag + badge), the PNR
      // roster (rebooking cells flip to NOT_APPLICABLE), the allocation view
      // (the stamp sets requiresHotel). One prefix invalidation covers them all.
      void qc.invalidateQueries({ queryKey: ['cases', caseUrn] });
    },
  });
}
